// src/pages/Dashboard.jsx
import { useMemo, useState } from 'react';
import { formatCurrency, monthLabel, currentMonthKey, getEntryBillKey, getBillKey, todayStr, addMonthsToKey } from '../services/billing';

const CAT_COLOR = { Alimentação:'#34d399', Moradia:'#6c8fff', Transporte:'#60a5fa', Saúde:'#f87171', Lazer:'#e879f9', Educação:'#fbbf24', Roupas:'#fb923c', Outros:'#94a3b8' };
const RAMPS = {
  blue:{dot:'#6c8fff'}, purple:{dot:'#a78bfa'}, teal:{dot:'#34d399'},
  coral:{dot:'#fb923c'}, pink:{dot:'#f472b6'}, amber:{dot:'#fbbf24'}, cash:{dot:'#34d399'},
};

export default function Dashboard({ cards, entries, onNavigate }) {
  const today = new Date();

  // Calcula o mês ativo pela lógica de vencimento
  const activeBillMonth = useMemo(() => {
    if (!cards.length) return currentMonthKey();
    const realCards = cards.filter(c => c.color !== 'cash');
    if (!realCards.length) return currentMonthKey();
    const lastDueDay = Math.max(...realCards.map(c => parseInt(c.dueDay)));
    const currentYM = currentMonthKey();
    if (today.getDate() > lastDueDay) {
      return addMonthsToKey(currentYM, 1);
    }
    return currentYM;
  }, [cards, today]);

  // Offset de navegação: 0 = mês ativo, -1 = mês anterior, etc.
  const [offset, setOffset] = useState(0);

  const now = useMemo(() => {
    return addMonthsToKey(activeBillMonth, offset);
  }, [activeBillMonth, offset]);

  const isActiveMonth = offset === 0;

  // Não deixa navegar para meses futuros além do ativo
  const canGoNext = offset < 0;
  const canGoPrev = true;

  const monthEntries = useMemo(
    () => entries.filter(e => getEntryBillKey(e, cards) === now),
    [entries, cards, now]
  );

  const cashEntries = useMemo(
    () => entries.filter(e => {
      const card = cards.find(c => c.id === e.cardId);
      return card?.color === 'cash' && e.date?.slice(0, 7) === now;
    }),
    [entries, cards, now]
  );

  const creditEntries = monthEntries.filter(e => {
    const card = cards.find(c => c.id === e.cardId);
    return card?.color !== 'cash';
  });

  const totalCredit = creditEntries.reduce((s, e) => s + e.val, 0);
  const totalCash   = cashEntries.reduce((s, e) => s + e.val, 0);
  const totalMonth  = totalCredit + totalCash;
  const totalAll    = entries.reduce((s, e) => s + e.val, 0);

  const nextDue = useMemo(() => {
    const realCards = cards.filter(c => c.color !== 'cash');
    if (!realCards.length) return null;
    return realCards.map(c => {
      const d = c.dueDay - today.getDate();
      return { name: c.name, days: d < 0 ? d + 30 : d, dueDay: c.dueDay };
    }).sort((a, b) => a.days - b.days)[0];
  }, [cards, today]);

  const catTotals = useMemo(() => {
    const all = [...creditEntries, ...cashEntries];
    const t = {};
    all.forEach(e => { t[e.cat] = (t[e.cat] || 0) + e.val; });
    return Object.entries(t).sort((a, b) => b[1] - a[1]);
  }, [creditEntries, cashEntries]);

  const cardTotals = useMemo(() => {
    const t = {};
    creditEntries.forEach(e => { t[e.cardId] = (t[e.cardId] || 0) + e.val; });
    return t;
  }, [creditEntries]);

  const catMax  = catTotals[0]?.[1] || 1;
  const cardMax = Math.max(...Object.values(cardTotals), 1);
  const realCards = cards.filter(c => c.color !== 'cash');

  return (
    <div className="content">
      <div className="page-header">
        <div>
          <div className="page-title">Dashboard</div>
          {/* Navegação de mês */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
            <button
              className="btn btn-sm"
              onClick={() => setOffset(o => o - 1)}
              title="Mês anterior"
            >
              <svg viewBox="0 0 24 24" style={{ width: 13, height: 13 }}><polyline points="15 18 9 12 15 6"/></svg>
            </button>

            <span style={{ fontSize: 14, color: 'var(--text-1)', fontWeight: 500, minWidth: 90, textAlign: 'center' }}>
              {monthLabel(now)}
              {isActiveMonth && (
                <span style={{ fontSize: 10, color: 'var(--accent)', marginLeft: 6, fontWeight: 400 }}>atual</span>
              )}
            </span>

            <button
              className="btn btn-sm"
              onClick={() => setOffset(o => o + 1)}
              disabled={!canGoNext}
              title="Próximo mês"
              style={{ opacity: canGoNext ? 1 : 0.3, cursor: canGoNext ? 'pointer' : 'default' }}
            >
              <svg viewBox="0 0 24 24" style={{ width: 13, height: 13 }}><polyline points="9 18 15 12 9 6"/></svg>
            </button>

            {!isActiveMonth && (
              <button
                className="btn btn-sm"
                onClick={() => setOffset(0)}
                style={{ fontSize: 11, color: 'var(--accent)' }}
              >
                Voltar ao atual
              </button>
            )}
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => onNavigate('novo')}>
          <svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Novo lançamento
        </button>
      </div>

      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-label">Total do mês</div>
          <div className="metric-value red">{formatCurrency(totalMonth)}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Cartões</div>
          <div className="metric-value red">{formatCurrency(totalCredit)}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">À vista</div>
          <div className="metric-value green">{formatCurrency(totalCash)}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Total histórico</div>
          <div className="metric-value">{formatCurrency(totalAll)}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Próx. vencimento</div>
          <div className="metric-value amber">
            {nextDue ? `Dia ${nextDue.dueDay} · ${nextDue.name}` : '—'}
          </div>
        </div>
      </div>

      <div className="two-col">
        <div>
          <div className="section-label">Por categoria</div>
          {catTotals.length ? catTotals.map(([cat, val]) => (
            <div className="bar-row" key={cat}>
              <div className="bar-label-row">
                <span>{cat}</span>
                <span style={{ color: 'var(--text-1)', fontWeight: 500 }}>{formatCurrency(val)}</span>
              </div>
              <div className="bar-track">
                <div className="bar-fill" style={{ width: `${(val / catMax * 100).toFixed(1)}%`, background: CAT_COLOR[cat] || '#888' }} />
              </div>
            </div>
          )) : <div style={{ color: 'var(--text-3)', fontSize: 12 }}>Sem dados neste mês</div>}
        </div>
        <div>
          <div className="section-label">Por cartão</div>
          {realCards.length ? realCards.map(c => {
            const r = RAMPS[c.color] || RAMPS.blue;
            const v = cardTotals[c.id] || 0;
            return (
              <div className="bar-row" key={c.id}>
                <div className="bar-label-row">
                  <span>{c.name}</span>
                  <span style={{ color: 'var(--text-1)', fontWeight: 500 }}>{formatCurrency(v)}</span>
                </div>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: `${(v / cardMax * 100).toFixed(1)}%`, background: r.dot }} />
                </div>
              </div>
            );
          }) : <div style={{ color: 'var(--text-3)', fontSize: 12 }}>Nenhum cartão</div>}
        </div>
      </div>

      <div className="section-label">Faturas — {monthLabel(now)}</div>
      {realCards.length ? realCards.map(c => {
        const r = RAMPS[c.color] || RAMPS.blue;
        const ces = creditEntries.filter(e => e.cardId === c.id);
        const tot = ces.reduce((s, e) => s + e.val, 0);
        const isOpen = now >= currentMonthKey();
        return (
          <div className="bill-card" key={c.id}>
            <div className="bill-card-header">
              <div>
                <div className="bill-card-name" style={{ color: r.dot }}>{c.name}</div>
                <div className="bill-card-period">Fecha dia {c.closeDay} · Vence dia {c.dueDay} · {monthLabel(now)}</div>
              </div>
              <span className={`badge ${isOpen ? 'badge-open' : 'badge-closed'}`}>
                {isOpen ? 'Aberta' : 'Fechada'}
              </span>
            </div>
            <div className="bill-card-total">{formatCurrency(tot)}</div>
            <div className="bill-card-sub">{ces.length} lançamento{ces.length !== 1 ? 's' : ''}</div>
          </div>
        );
      }) : (
        <div style={{ color: 'var(--text-3)', fontSize: 13, textAlign: 'center', padding: '2rem', background: 'var(--bg-2)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
          Nenhum cartão cadastrado.{' '}
          <span style={{ color: 'var(--accent)', cursor: 'pointer' }} onClick={() => onNavigate('cartoes')}>Adicionar cartão →</span>
        </div>
      )}

      {totalCash > 0 && (
        <>
          <div className="section-label" style={{ marginTop: '1.5rem' }}>À vista — {monthLabel(now)}</div>
          <div className="bill-card">
            <div className="bill-card-header">
              <div>
                <div className="bill-card-name" style={{ color: '#34d399' }}>💵 Dinheiro / À vista</div>
                <div className="bill-card-period">{monthLabel(now)}</div>
              </div>
            </div>
            <div className="bill-card-total">{formatCurrency(totalCash)}</div>
            <div className="bill-card-sub">{cashEntries.length} lançamento{cashEntries.length !== 1 ? 's' : ''}</div>
          </div>
        </>
      )}
    </div>
  );
}